import { BroadcastService } from '../../common/services/broadcast/broadcast.service';
import { CachingService } from '../../common/services/caching/caching.service';
import { CommonEvents } from '../../common/enums/common-events.enum';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ContextButtonService } from '../../common/services/context-button/context-button.service';
import { debounceTime, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { IAggregateLocationViewModel } from '../../user/models/iaggregate-location-view-model';
import { ModuleSearchService } from '../services/module-search.service';
import { PortalEvents } from '../../portal/enums/portal-events.enum';
import { Subscription } from 'rxjs';
import { UserService } from '../../user/services/user.service';

@Component({
    selector: 'cas-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnInit, OnDestroy {
    searchByUserInput = false;
    locationCtrl: FormControl = new FormControl();
    locations: IAggregateLocationViewModel[] = [];
    isOpened = false;
    isLoading = true;

    private cachedLocations: any[];
    private subscriptions = new Subscription();

    constructor(
        private broadcastService: BroadcastService,
        private cachingService: CachingService,
        private contextButtonService: ContextButtonService,
        private moduleSearchService: ModuleSearchService,
        private userService: UserService,
    ) {}

    ngOnInit() {
        this.initializeFormSubscription();
        this.getUserCachedLocations();
        this.initializePortalEventSubscription();
        this.initializeOpenSearchbarSubscription();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onSearchbarBlur() {
        setTimeout(() => {
            this.isOpened = false;
        }, 100);
    }

    search(description: string) {
        this.subscriptions.add(
            this.getLocationList(description)
                .subscribe(
                    (response: any) => {
                        this.locations = response.result.map(location => {
                            return {
                                id: location.locationId,
                                description: location.displayText
                            } as IAggregateLocationViewModel;
                        });
                        const foundLocation = this.findLocationByDescription(description);
                        if (foundLocation) {
                            this.selectCachedLocation(foundLocation);
                        }
                        this.isLoading = false;
                    },
                    () => this.isLoading = false
                ));
    }

    selectLocation(location: IAggregateLocationViewModel) {
        this.selectCachedLocation(location);
        this.userService.changeLocation(location.id);
        this.toggleSearching();
        return;
    }

    displayFn(location: IAggregateLocationViewModel): string {
        if (!location) {
            return;
        }
        return location.description;
    }

    toggleSearching() {
        this.isOpened = !this.isOpened;
        this.broadcastService.broadcast(PortalEvents.SearchStarted, this.isOpened);
        this.contextButtonService.toolbarHidden = this.isOpened;
    }

    private putToRecent(location: IAggregateLocationViewModel) {
        this.cachedLocations = this.cachedLocations ? this.cachedLocations : [];
        this.cachedLocations = this.cachedLocations.filter(l => l.id !== location.id);
        this.cachedLocations.unshift(location);
        this.cachingService.put(this.userService.cacheId, this.cachedLocations);
    }

    private getUserCachedLocations() {
        this.subscriptions.add(
            this.cachingService
                .get(this.userService.cacheId)
                .pipe(
                    take(1),
                    tap((cachedLocations) => this.assignCachedLocations(cachedLocations as any)),
                    switchMap(() => this.userService.activeLocation),
                    take(1),
                    tap((activeLocation) => this.selectCachedLocation(activeLocation)),
                    tap(() => this.initializeActiveLocationSubsription()),
                )
                .subscribe());
    }

    private initializeOpenSearchbarSubscription() {
        this.subscriptions.add(this.broadcastService.on(CommonEvents.OpenSearchBar)
            .subscribe(() => {
                this.toggleSearching();
            }
        ));
    }

    private initializePortalEventSubscription() {
        this.subscriptions.add(this.broadcastService
            .on(PortalEvents.locationSearch)
            .subscribe((location: IAggregateLocationViewModel) => {
                const locationAlreadyLoaded = this.findLocationById(location.id);
                if (locationAlreadyLoaded) {
                    this.selectCachedLocation(location);
                } else {
                    this.search(location.description);
                }
            }));
    }

    private initializeActiveLocationSubsription() {
        this.subscriptions.add(this.userService.activeLocation
            .subscribe((activeLocation) => {
                const locationAlreadyLoaded = this.findLocationById(activeLocation.id);
                if (locationAlreadyLoaded) {
                    this.selectCachedLocation(activeLocation);
                } else {
                    this.search(activeLocation.description);
                }
            }));
    }

    private initializeFormSubscription() {
        this.subscriptions.add(
            this.locationCtrl.valueChanges
                .pipe(
                    distinctUntilChanged(),
                    debounceTime(300),
                    tap((value) => {
                        this.searchByUserInput = typeof value === 'string';
                    }),
                    map(value => typeof value === 'string' ? value : value.description),
                )
                .subscribe((value) => {
                    if (this.searchByUserInput) {
                        this.search(value);
                    }
                }));
    }

    private findLocationById(id: number) {
        return this.locations.find((location) => id === location.id);
    }

    private findLocationByDescription(locationName: string) {
        return this.locations.find((location) => locationName === location.description);
    }

    private selectCachedLocation(location: IAggregateLocationViewModel) {
        const foundLocation = this.findLocationById(location.id);
        if (foundLocation) {
            this.locationCtrl.patchValue(foundLocation);
            this.putToRecent(location);
        }
    }

    private assignCachedLocations(cachedLocations: any[]) {
        if (cachedLocations) {
            this.locations = cachedLocations;
            this.cachedLocations = cachedLocations;
        }
        this.isLoading = false;
    }

    private getLocationList(value: string) {
        return this.moduleSearchService
            .getList({
                contains: value,
                isLinked: true,
                pageIndex: 0,
                pageSize: 20
            });
    }
}
